import {
  type AriaKey,
  KdsToggleButtonGroup,
  Sidebar,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { clsx } from "clsx";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import KazaarTextLogo from "@/assets/logos/kazaar_logo.svg";
import { Header } from "@/components/Header";
import { useIsOMS } from "@/hooks/useIsOMS";
import { userAtom } from "@/store";
import { CLIENT, OMS } from "./Layout.constants";
import { type LayoutProps, type SideBarItem } from "./Layout.types";

export const Layout = ({ sideBarItems, className, children }: LayoutProps) => {
  const isOMS = useIsOMS();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const location = useLocation();
  const userAtomValue = useAtomValue(userAtom);

  const [selectedKeys, setSelectedKeys] = useState<Set<AriaKey>>(
    new Set([isOMS ? OMS : CLIENT])
  );

  const topSideBarItems = useMemo(() => {
    return sideBarItems.filter((item) => !item.isBottomItem);
  }, [sideBarItems]);

  const bottomSideBarItems = useMemo(() => {
    return sideBarItems.filter((item) => item.isBottomItem);
  }, [sideBarItems]);

  const renderSidebarItem = (item: SideBarItem) => {
    const isActive = location.pathname.includes(item.href);
    const queryParams = isActive ? location.search : "";

    if (item.isRequireFeatureFlagAdmin && !userAtomValue?.isFeatureFlagAdmin) {
      return null;
    }

    const itemProps = {
      ...item,
      queryParams,
      active: location.pathname.includes(item.href),
      ...(item.isOpeningInNewTab && {
        target: "_blank",
        rel: "noopener noreferrer",
      }),
    };

    return (
      <Sidebar.Item key={item.href} {...itemProps}>
        {item.name}
      </Sidebar.Item>
    );
  };

  const handleSelectionChange = (selectedKeys: Set<AriaKey>) => {
    setSelectedKeys(selectedKeys);

    const isNavigatingToOMS = selectedKeys.has(OMS);
    void navigate(isNavigatingToOMS ? "/oms/dashboard" : "/dashboard");
  };

  return (
    <div className={clsx("h-screen w-screen overflow-hidden", className)}>
      <Header logo={KazaarTextLogo} avatarUrl={userAtomValue?.avatar} />

      <div className="flex">
        <Sidebar>
          <div className="flex h-full w-full flex-col items-center justify-between">
            <div className="flex w-full flex-col items-center gap-sp-md">
              {userAtomValue?.role === "CSM" && (
                <div className="w-full px-sp-md">
                  <KdsToggleButtonGroup
                    options={[
                      {
                        id: "oms",
                        label: t("navigation.application.goToOms"),
                        isSelected: Boolean(isOMS),
                      },
                      {
                        id: "client",
                        label: t("navigation.application.goToClient"),
                        isSelected: !isOMS,
                      },
                    ]}
                    onSelectionChange={handleSelectionChange}
                    fullWidth
                    selectionMode="single"
                    selectedKeys={selectedKeys}
                  />
                </div>
              )}

              <div className="flex w-full flex-col">
                {topSideBarItems.map(renderSidebarItem)}
              </div>
            </div>

            <div className="mb-12 flex w-full flex-col">
              {bottomSideBarItems.map(renderSidebarItem)}
            </div>
          </div>
        </Sidebar>

        <div
          className={clsx(
            "relative h-[calc(100vh-100px)] w-full overflow-hidden bg-slate-50 sm:h-[calc(100vh-60px)]"
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
